import { UserDetails, getDecryptedValue } from 'common';
import {
  startOpenReplayRecording,
  stopOpenReplaySession,
} from 'common/openreplay/openreplay';

const PRE_LOGIN_IDLE_TIMEOUT_MS = 10 * 60 * 1000;
// Wait this long after activity resumes before (re)starting the tracker, so we
// don't spin the recorder up on a stray event.
const PRE_LOGIN_START_THRESHOLD_MS = 10 * 1000;
const PRE_LOGIN_ACTIVITY_EVENTS = [
  'click',
  'touchstart',
  'scroll',
  'keydown',
  'mousemove',
  'wheel',
] as const;

let preLoginIdleTimerId: ReturnType<typeof setTimeout> | null = null;
let preLoginStartTimerId: ReturnType<typeof setTimeout> | null = null;
let preLoginIdleMonitoringActive = false;

const clearPreLoginIdleTimer = (): void => {
  if (preLoginIdleTimerId) {
    clearTimeout(preLoginIdleTimerId);
    preLoginIdleTimerId = null;
  }
};

const clearPreLoginStartTimer = (): void => {
  if (preLoginStartTimerId) {
    clearTimeout(preLoginStartTimerId);
    preLoginStartTimerId = null;
  }
};

const handlePreLoginIdleTimeout = async (): Promise<void> => {
  const latestPersonId = await getDecryptedValue(UserDetails.personId);
  if (!latestPersonId) {
    // Stop without restarting: the next activity or login starts a fresh session
    await stopOpenReplaySession({ restart: false });
  }
};

function schedulePreLoginIdleTimeout(): void {
  clearPreLoginIdleTimer();
  preLoginIdleTimerId = setTimeout(() => {
    void handlePreLoginIdleTimeout();
  }, PRE_LOGIN_IDLE_TIMEOUT_MS);
}

function handlePreLoginActivity(): void {
  // On any user interaction, ensure recording is live. startOpenReplayRecording
  // is idempotent (it no-ops when already recording), so if the session was
  // ended by the idle timeout this transparently restarts it — rebuilding the
  // tracker and re-attaching Assist so co-browse works without a hard refresh.
  //
  // Defer the start by PRE_LOGIN_START_THRESHOLD_MS so a stray event doesn't
  // immediately spin the recorder back up. The first event arms the timer; later
  // events while it is pending are ignored so the start still fires on schedule.
  if (!preLoginStartTimerId) {
    preLoginStartTimerId = setTimeout(() => {
      preLoginStartTimerId = null;
      void startOpenReplayRecording();
    }, PRE_LOGIN_START_THRESHOLD_MS);
  }
  schedulePreLoginIdleTimeout();
}

export const startPreLoginIdleMonitoring = (): void => {
  if (typeof globalThis === 'undefined') {
    return;
  }

  if (!preLoginIdleMonitoringActive) {
    PRE_LOGIN_ACTIVITY_EVENTS.forEach((eventName) => {
      globalThis.addEventListener(eventName, handlePreLoginActivity, {
        passive: true,
      });
    });
    preLoginIdleMonitoringActive = true;
  }

  schedulePreLoginIdleTimeout();
};

export const stopPreLoginIdleMonitoring = (): void => {
  clearPreLoginIdleTimer();
  clearPreLoginStartTimer();
  if (!preLoginIdleMonitoringActive || typeof globalThis === 'undefined') {
    preLoginIdleMonitoringActive = false;
    return;
  }

  PRE_LOGIN_ACTIVITY_EVENTS.forEach((eventName) => {
    globalThis.removeEventListener(eventName, handlePreLoginActivity);
  });
  preLoginIdleMonitoringActive = false;
};
